"use client";

/**
 * Input that allows multiple, distinct values (eg tags, email addresses) with optional validation.
 */

import React, { useCallback, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import AutosizeInput from "react-input-autosize";
import { useCombobox } from "downshift";

import KeyNames from "../../constants/keyNames";

import Flex from "../Flex";
import Icon from "../Icon";
import PopupMenu from "../PopupMenu";
import Text from "../Text";
import Tooltip from "../Tooltip";
import { getBorderRadius } from "../utils";
import factory from "../../testUtils/factory";
import { noop } from "lodash";
import { BoxProps } from "../Box";

type InputWrapperProps = {
  isFocused: boolean;
};
const InputValueWrapper = styled(Flex).attrs<InputWrapperProps>(props => {
  return {
    background: "white",
    px: "xs",
    py: "xxs",
    flex: "1 1 auto",
    border: `1px ${props.isFocused ? "focus" : "border"} solid`,
    borderRadius: getBorderRadius("default", null, props),
    alignItems: "center",
  };
})<InputWrapperProps>`
  outline: none;
  cursor: text;
`;

const ItemWrapper = styled(Flex).attrs({
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  flex: "1 1 auto",
})``;

const inputClassName = "input-style";
const StyledAutosizeInput = styled(AutosizeInput).attrs({ inputClassName })`
  flex: 1;
  /* min-width: 75px; */
  .${inputClassName} {
    width: 100% !important;
    min-width: 75px;
    padding: 0;
    margin: 4px;
    border: none;
    outline: none;
    cursor: inherit;
    background-color: transparent;
  }
`;

type TokenComponentProps = { isValid: boolean; title: string; children: React.ReactNode };
const DefaultTokenComponent = styled(Flex).attrs<TokenComponentProps>(props => {
  return {
    m: "xs",
    pl: "s",
    pr: "xs",
    borderRadius: "default",
    backgroundColor: props.isValid ? "primaryColor" : "error",
    alignItems: "center",
  };
})<TokenComponentProps>``;

type SuggestionComponentProps<S> = { isHighlighted: boolean; title: string; suggestion: S };

const DefaultSuggestionComponent = <S extends TokenInputSuggestion>({
  title,
  isHighlighted,
  ...rest
}: SuggestionComponentProps<S>) => {
  return <PopupMenu.Item isActive={isHighlighted} title={title} {...rest} />;
};

export type TokenInputToken = {
  id: string;
  value: string;
  isValid: boolean;
  invalidReason?: string;
};

export type TokenInputSuggestion = {
  value: string;
};

type Props<
  T extends TokenInputToken = TokenInputToken,
  S extends TokenInputSuggestion = TokenInputSuggestion,
> = {
  inputValue?: string;
  onChange?: (value: string) => void;
  autoFocus?: boolean;
  // Tokens
  tokens: Array<T>;
  onAddToken?: (item: S | string) => void;
  onRemoveToken?: (index: number) => void;

  // Suggestions: optionally provide suggestions to show in an autocomplete dropdown fromt the input
  suggestions?: S[];
  renderEmptySuggestions?: () => React.ReactNode;
  TokenComponent?: React.FC<TokenComponentProps>;
  SuggestionComponent?: React.FC<SuggestionComponentProps<S>>;

  children?: React.ReactNode;
} & BoxProps;

const TokenInput = <T extends TokenInputToken, S extends TokenInputSuggestion>({
  inputValue: providedValue = null,
  onChange,
  autoFocus,
  tokens,
  onAddToken,
  onRemoveToken: onRemoveToken,
  suggestions,
  renderEmptySuggestions,
  TokenComponent = DefaultTokenComponent,
  SuggestionComponent = DefaultSuggestionComponent,
  children,
  ...styleProps
}: Props<T, S>) => {
  const [cachedValue, setCachedValue] = useState("");
  const [isFocused, setFocused] = useState(false);
  const currentValue = providedValue === null ? cachedValue : providedValue;
  const popupSource = useMemo(() => factory.genUUID(), []);
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  const addToken = useCallback(
      (text: S | string) => {
        if (!text || !onAddToken) return;
        onAddToken(text);
        setCachedValue("");
      },
      [onAddToken]
    ),
    removeToken = useCallback(
      (index: number) => {
        onRemoveToken(index);
      },
      [onRemoveToken]
    ),
    popToken = useCallback(() => {
      if (tokens.length === 0) return;
      return removeToken(tokens.length - 1);
    }, [tokens, removeToken]);

  const { getInputProps, getMenuProps, getItemProps, isOpen, highlightedIndex } = useCombobox({
    inputValue: currentValue,
    items: suggestions ?? [],
    defaultIsOpen: false,
    itemToString: () => "",
    onStateChange({ inputValue: newInputValue, type, selectedItem: newSelectedItem }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          addToken(newSelectedItem ?? newInputValue ?? currentValue);
          setCachedValue("");
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnd:
          addToken(newInputValue);
          break;
        case useCombobox.stateChangeTypes.InputBlur:
          addToken(newInputValue ?? currentValue);
          setFocused(false);
          break;
        case useCombobox.stateChangeTypes.InputChange: {
          let endOfValue: string | null = null;
          if (newInputValue) {
            if (newInputValue.endsWith(", ")) {
              endOfValue = ", ";
            } else if (newInputValue.endsWith(" ")) {
              endOfValue = " ";
            }
            if (endOfValue) {
              const valueToAdd = newInputValue.slice(0, -endOfValue.length).trim();
              if (valueToAdd) {
                addToken(valueToAdd);
                return;
              }
            }
          }
          onChange?.(newInputValue);
          setCachedValue(newInputValue);
          break;
        }
        default:
          break;
      }
    },
  });

  const inputProps = getInputProps();
  const onKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
          case KeyNames.backspace:
          case KeyNames.delete: {
            if (currentValue === "") {
              e.preventDefault();
              popToken();
              return;
            }
            break;
          }
          default:
            break;
        }
        inputProps.onKeyDown(e);
      },
      [currentValue, inputProps, popToken]
    ),
    onFocus = useCallback(() => {
      setFocused(true);
    }, []);

  function renderSuggestions() {
    const suggestionCount = suggestions?.length ?? 0;
    const emptyComponent = suggestionCount === 0 ? renderEmptySuggestions?.() : null;
    const hasContent = Boolean(suggestionCount > 0 || emptyComponent);
    const width = inputWrapperRef.current?.getBoundingClientRect().width;

    return (
      <PopupMenu
        source={popupSource}
        open={isOpen && hasContent}
        width={width}
        {...getMenuProps()}
        close={noop}>
        {emptyComponent && <PopupMenu.Section>{emptyComponent}</PopupMenu.Section>}
        {suggestionCount > 0 && (
          <PopupMenu.Section>
            {suggestions?.map((sugg, index) => (
              <span
                key={`item-${index}`}
                {...getItemProps({
                  item: sugg,
                  index,
                })}>
                <SuggestionComponent
                  suggestion={sugg}
                  isHighlighted={highlightedIndex === index}
                  title={sugg.value}
                />
              </span>
            ))}
          </PopupMenu.Section>
        )}
      </PopupMenu>
    );
  }

  return (
    <>
      <InputValueWrapper
        data-popup-source={popupSource}
        ref={inputWrapperRef}
        isFocused={isFocused}
        tabIndex={-1}
        {...styleProps}>
        <ItemWrapper>
          {tokens?.map((token, idx) => (
            <TokenComponent
              data-tooltip-source={token.id}
              isValid={token.isValid}
              key={`item-${token.id}`}
              title={token.isValid ? "Valid email" : `Invalid: ${token.invalidReason}`}>
              <Text color={token.isValid ? "primaryColorTint" : "white"} pr="xs">
                {token.value}
              </Text>
              <Icon
                size="16"
                iconId="action.close"
                fill="primaryColorTint"
                onClick={() => removeToken(idx)}
              />
              {token.invalidReason && (
                <Tooltip direction={Tooltip.Direction.Top} source={token.id}>
                  {token.invalidReason}
                </Tooltip>
              )}
            </TokenComponent>
          )) || null}
          <StyledAutosizeInput
            {...inputProps}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            autoFocus={autoFocus}
          />
        </ItemWrapper>
        <div style={{ whiteSpace: "nowrap" }}>{children}</div>
      </InputValueWrapper>
      {renderSuggestions()}
    </>
  );
};

export default TokenInput;
